import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { useUpdateEmployee } from "@/hooks/use-update-employee";
import type { DBUser } from "@/lib/db-types";
import { SPECIALIZATION_LABELS, SPECIALIZATION_OPTIONS } from "@/lib/constants";

interface SettingsTabProps {
  user: DBUser;
  /** Timepris T&M vises og redigeres bare av admin. */
  showRates: boolean;
}

const NONE = "none";

function formFrom(user: DBUser) {
  return {
    job_title: user.job_title ?? "",
    specialization: user.specialization ?? "",
    birthday: user.birthday ?? "",
    hire_date: user.hire_date ?? "",
    monthly_capacity_hours: user.monthly_capacity_hours?.toString() ?? "",
    target_billing_rate: user.target_billing_rate?.toString() ?? "",
    hourly_sales_rate: user.hourly_sales_rate?.toString() ?? "",
  };
}

type Form = ReturnType<typeof formFrom>;

const toNumber = (v: string) => (v ? parseFloat(v) : null);

function Field({ id, label, help, children }: { id: string; label: string; help?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {help && <span className="text-xs text-muted-foreground">{help}</span>}
    </div>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-[11.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{title}</legend>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </fieldset>
  );
}

export function SettingsTab({ user, showRates }: SettingsTabProps) {
  const updateMutation = useUpdateEmployee();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Form>(() => formFrom(user));

  const saved = formFrom(user);
  const hasChanges = (Object.keys(form) as (keyof Form)[]).some((k) => form[k] !== saved[k]);
  const set = (key: keyof Form) => (value: string) => setForm((f) => ({ ...f, [key]: value }));
  const input = (key: keyof Form) => ({
    id: `employee-${key}`,
    value: form[key],
    disabled: !editing,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(key)(e.target.value),
  });

  const startEditing = () => {
    setForm(formFrom(user));
    setEditing(true);
  };

  const save = () => {
    updateMutation.mutate(
      {
        userId: user.id,
        updates: {
          job_title: form.job_title || null,
          specialization: form.specialization || null,
          birthday: form.birthday || null,
          hire_date: form.hire_date || null,
          monthly_capacity_hours: toNumber(form.monthly_capacity_hours),
          target_billing_rate: toNumber(form.target_billing_rate),
          ...(showRates ? { hourly_sales_rate: toNumber(form.hourly_sales_rate) } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success("Ansatt oppdatert");
          setEditing(false);
        },
        onError: (err) => toast.error((err as Error).message),
      },
    );
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
          <h2 className="font-display text-base font-semibold">Innstillinger</h2>
          {!editing ? (
            <Button variant="outline" size="sm" onClick={startEditing} className="gap-1.5">
              <SrIcon name="edit2" className="h-3.5 w-3.5" />
              Rediger
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={updateMutation.isPending}>
                Avbryt
              </Button>
              <Button size="sm" onClick={save} disabled={!hasChanges || updateMutation.isPending} className="gap-1.5">
                <SrIcon
                  name={updateMutation.isPending ? "loader2" : "check"}
                  className={updateMutation.isPending ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"}
                />
                Lagre
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-6 border-t border-border px-5 py-5">
          <Group title="Person">
            <Field id="employee-job_title" label="Stillingstittel">
              <Input {...input("job_title")} placeholder="F.eks. Fotograf" />
            </Field>
            <Field id="employee-specialization" label="Fagkompetanse">
              <Select
                value={form.specialization || NONE}
                onValueChange={(v) => set("specialization")(v === NONE ? "" : v)}
                disabled={!editing}
              >
                <SelectTrigger id="employee-specialization">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Ikke satt</SelectItem>
                  {SPECIALIZATION_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {SPECIALIZATION_LABELS[s] ?? s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field id="employee-birthday" label="Fødselsdato">
              <Input type="date" {...input("birthday")} />
            </Field>
          </Group>

          <Group title="Arbeid">
            <Field id="employee-hire_date" label="Ansettelsesdato">
              <Input type="date" {...input("hire_date")} />
            </Field>
            <Field id="employee-monthly_capacity_hours" label="Kapasitet (timer per måned)" help="Brukes i faktureringsgraden">
              <Input type="number" min={0} step={0.25} className="tabular-nums" placeholder="Ikke satt" {...input("monthly_capacity_hours")} />
            </Field>
            <Field id="employee-target_billing_rate" label="Faktureringsmål (%)">
              <Input type="number" min={0} max={200} step={1} className="tabular-nums" placeholder="Ikke satt" {...input("target_billing_rate")} />
            </Field>
          </Group>

          {showRates && (
            <Group title="Pris">
              <Field id="employee-hourly_sales_rate" label="Timepris T&M (kr)" help="Bare synlig for admin">
                <Input type="number" min={0} className="tabular-nums" placeholder="Ikke satt" {...input("hourly_sales_rate")} />
              </Field>
            </Group>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
